import React, { useState } from "react";
import { useMutation } from "react-query";
import { Button, notification, Popconfirm, Tooltip, Typography } from "antd";
import { Common } from "@secondcloset/types";
import { setupCorrespondingHubbleMerchant } from "../../../../../api/accounts/organization";
import "./HubbleMerchantSection.scss";

const { Text } = Typography;

interface HubbleMerchantSectionProps {
  organizationID: string;
  initialHubbleOrganizationID?: string;
  fbbFulfillmentOrgID?: string;
}

const getHubbleOrganizationID = (organization: Common.Organization): string | undefined => {
  return organization?.configurations_attributes?.find((config) => (config.key as string) === "hubble_organization_id")
    ?.value;
};

const HubbleMerchantSection: React.FC<HubbleMerchantSectionProps> = ({
  organizationID,
  initialHubbleOrganizationID,
  fbbFulfillmentOrgID,
}) => {
  const [createdHubbleOrganizationID, setCreatedHubbleOrganizationID] = useState<string>();
  const hubbleOrganizationID = createdHubbleOrganizationID || initialHubbleOrganizationID;

  const { mutate: createHubbleMerchant, isLoading } = useMutation(
    () => setupCorrespondingHubbleMerchant(organizationID),
    {
      onSuccess(organization) {
        const newHubbleOrganizationID = getHubbleOrganizationID(organization);
        setCreatedHubbleOrganizationID(newHubbleOrganizationID);
        notification.success({
          message: "Hubble merchant created.",
          description: newHubbleOrganizationID
            ? `Hubble organization ID: ${newHubbleOrganizationID}`
            : "The merchant was created in Hubble.",
          duration: 20,
        });
      },
      onError(e: Error | string) {
        notification.error({
          message: "Error creating Hubble merchant:",
          description: typeof e === "string" ? e : e.message,
          duration: 20,
        });
      },
    }
  );

  const renderHubbleOrganizationID = () => (
    <Text data-testid="hubble-organization-id">
      Hubble organization ID:{" "}
      <Text code copyable>
        {hubbleOrganizationID}
      </Text>
    </Text>
  );

  const renderCreateButton = () => {
    const isLinkedToFulfillmentOrg = !!fbbFulfillmentOrgID;

    return (
      <>
        <Text type="secondary">
          Create the corresponding merchant in Hubble (Boxknight) for this organization. The new merchant ID will be
          saved to this organization&apos;s configurations.
        </Text>
        <div className="button-container">
          <Popconfirm
            title="This will create a merchant in Hubble for this organization. Continue?"
            okText="Create"
            onConfirm={() => createHubbleMerchant()}
            disabled={isLoading || isLinkedToFulfillmentOrg}
          >
            <Tooltip
              title={
                isLinkedToFulfillmentOrg
                  ? "This organization is already linked to a fulfillment organization (fbb_fulfillment_org_id is set), so it cannot have its own Hubble merchant."
                  : undefined
              }
            >
              <Button
                type="primary"
                loading={isLoading}
                disabled={isLinkedToFulfillmentOrg}
                data-testid="create-hubble-merchant-button"
              >
                Create Hubble Merchant
              </Button>
            </Tooltip>
          </Popconfirm>
        </div>
      </>
    );
  };

  return (
    <div className="hubble-merchant-section">
      <h3 className="section-header">Hubble Merchant</h3>
      {hubbleOrganizationID ? renderHubbleOrganizationID() : renderCreateButton()}
    </div>
  );
};

export default HubbleMerchantSection;
